import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { APP_CONFIG } from '@cardapio-online/config';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  type NotificationSettings,
  type NotificationSettingsResponse,
  type UpdateNotificationSettingsPayload,
} from '../models/notification-settings.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationSettingsService {
  private static readonly STORAGE_VERSION = 1;
  private static readonly MIN_DURATION_SECONDS = 3;
  private static readonly MAX_DURATION_SECONDS = 60;

  private readonly http = inject(HttpClient);
  private readonly cfg = inject(APP_CONFIG);

  private readonly settingsSignal = signal<NotificationSettings>(
    this.readStoredSettings()
  );

  readonly settings = computed(() => this.settingsSignal());
  readonly scopeLabel = computed(() => this.resolveScope());

  private readonly basePath = '/notifications/settings';

  snapshot(): NotificationSettings {
    return this.settingsSignal();
  }

  load(): Observable<NotificationSettings> {
    return this.http.get<NotificationSettingsResponse>(this.basePath).pipe(
      map((response) => this.normalize(response.data)),
      tap((settings) => this.commit(settings)),
      catchError(() => of(this.settingsSignal()))
    );
  }

  update(
    next: Partial<UpdateNotificationSettingsPayload>
  ): Observable<NotificationSettings> {
    const payload = this.buildPayload(next);

    return this.http.put<NotificationSettingsResponse>(this.basePath, payload).pipe(
      map((response) => this.normalize(response.data)),
      tap((settings) => this.commit(settings))
    );
  }

  reset(): Observable<NotificationSettings> {
    return this.update(DEFAULT_NOTIFICATION_SETTINGS);
  }

  private buildPayload(
    next: Partial<UpdateNotificationSettingsPayload>
  ): UpdateNotificationSettingsPayload {
    const merged = {
      ...this.settingsSignal(),
      ...next,
    };

    return {
      enabled: merged.enabled,
      autoDismiss: merged.autoDismiss,
      durationSeconds: this.normalizeDurationSeconds(merged.durationSeconds),
      showUnreadBadge: merged.showUnreadBadge,
      showDashboardWidget: merged.showDashboardWidget,
    };
  }

  private normalize(
    raw: Partial<NotificationSettings> | null | undefined
  ): NotificationSettings {
    return {
      enabled: raw?.enabled ?? DEFAULT_NOTIFICATION_SETTINGS.enabled,
      autoDismiss: raw?.autoDismiss ?? DEFAULT_NOTIFICATION_SETTINGS.autoDismiss,
      durationSeconds: this.normalizeDurationSeconds(raw?.durationSeconds),
      showUnreadBadge:
        raw?.showUnreadBadge ?? DEFAULT_NOTIFICATION_SETTINGS.showUnreadBadge,
      showDashboardWidget:
        raw?.showDashboardWidget ??
        DEFAULT_NOTIFICATION_SETTINGS.showDashboardWidget,
      updatedAt: raw?.updatedAt?.trim() || null,
    };
  }

  private commit(settings: NotificationSettings): void {
    this.settingsSignal.set(settings);
    this.persist(settings);
  }

  private readStoredSettings(): NotificationSettings {
    if (typeof window === 'undefined') {
      return DEFAULT_NOTIFICATION_SETTINGS;
    }

    const raw = localStorage.getItem(this.getStorageKey());

    if (!raw) {
      return DEFAULT_NOTIFICATION_SETTINGS;
    }

    try {
      const parsed = JSON.parse(raw) as {
        version?: number;
        settings?: Partial<NotificationSettings>;
      };

      if (parsed.version !== NotificationSettingsService.STORAGE_VERSION) {
        return DEFAULT_NOTIFICATION_SETTINGS;
      }

      return this.normalize(parsed.settings);
    } catch {
      return DEFAULT_NOTIFICATION_SETTINGS;
    }
  }

  private persist(settings: NotificationSettings): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(
      this.getStorageKey(),
      JSON.stringify({
        version: NotificationSettingsService.STORAGE_VERSION,
        settings,
      })
    );
  }

  private getStorageKey(): string {
    return `menuhub:${this.resolveScope()}:admin:notification-settings`;
  }

  private resolveScope(): string {
    const configuredTenant = this.cfg.tenantSlug?.trim().toLowerCase();

    if (configuredTenant) {
      return configuredTenant;
    }

    if (typeof window === 'undefined') {
      return 'server';
    }

    return window.location.hostname.trim().toLowerCase() || 'default';
  }

  private normalizeDurationSeconds(value: number | null | undefined): number {
    const fallback = DEFAULT_NOTIFICATION_SETTINGS.durationSeconds;

    if (typeof value !== 'number' || Number.isNaN(value)) {
      return fallback;
    }

    return Math.min(
      NotificationSettingsService.MAX_DURATION_SECONDS,
      Math.max(NotificationSettingsService.MIN_DURATION_SECONDS, Math.round(value))
    );
  }
}
